import { useState, type ComponentProps, type PropsWithChildren } from 'react'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import {
  END_OF_EPISODE,
  showKeys,
  shows,
  START_OF_EPISODE,
  type Results,
  type ShowKey,
} from '../shared'
import { getEpisodesBySeason } from '../macros' with { type: 'macro' }
import type { SearchPayload } from '../search'
import brooklynNineNineLogo from '../assets/brooklyn-nine-nine.png'
import theOfficeLogo from '../assets/the-office.svg'
import newGirlLogo from '../assets/new-girl.svg'
import { DocumentTitle } from './DocumentTitle'

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 5 * 60 * 1000 } } })
const seasonSelectorData = await getEpisodesBySeason()
const showUiSettings = {
  'brooklyn-nine-nine': {
    headerImage: brooklynNineNineLogo,
    headerImageStyles: '-my-4 mx-auto max-h-36',
  },
  'new-girl': {
    headerImage: newGirlLogo,
    headerImageStyles: '-my-4 mx-auto max-h-36',
  },
  'the-office': {
    headerImage: theOfficeLogo,
    headerImageStyles: '',
  },
} as const satisfies Record<ShowKey, Record<string, unknown>>

type SortOrder = 'relevance' | 'chronological'

export function App({ showKey }: { showKey: ShowKey }) {
  return (
    <QueryClientProvider client={queryClient}>
      <DocumentTitle title={shows[showKey]} />
      <Search showKey={showKey} />
      <Footer showKey={showKey} />
    </QueryClientProvider>
  )
}

function Search({ showKey }: ComponentProps<typeof App>) {
  const [searchQuery, setSearchQuery] = useState('')
  const [linesOfContext, setLinesOfContext] = useState(2)
  const [seasonFilter, setSeasonFilter] = useState('')
  const [episodeFilter, setEpisodeFilter] = useState('')
  const [sortOrder, setSortOrder] = useState<SortOrder>('relevance')

  const { data, isFetching, isPending } = useQuery({
    queryKey: ['repoData', { searchQuery, linesOfContext, episodeFilter, seasonFilter }],
    queryFn: ({ signal }) => {
      return fetchSearchResults(
        searchQuery,
        { linesOfContext, episodeFilter, seasonFilter },
        { signal, showKey },
      )
    },
    placeholderData: (previousData) => {
      if (!searchQuery) return undefined
      return previousData
    },
    enabled: !!searchQuery,
  })

  return (
    <div className="max-w-2xl mx-auto p-8 relative z-10 min-h-[calc(100vh-2.5rem-1px)]">
      <div className="mb-8 h-28 flex items-end">
        <div className="flex-1">
          <img
            src={showUiSettings[showKey].headerImage}
            alt={`${shows[showKey]} logo`}
            className={`block ${showUiSettings[showKey].headerImageStyles}`}
          />
        </div>
      </div>
      <div>
        <div className="mb-4 relative">
          <label htmlFor="query" className="sr-only">
            Find a Line
          </label>
          <input
            id="query"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                setSearchQuery('')
              }
            }}
            className="block shadow bg-white rounded-xl py-4 px-6 text-black mt-1 w-full"
            placeholder="Search a word or phrase"
          />
          {isFetching ? (
            <div className="absolute right-3.5 top-4 -mt-1.25">
              <svg
                aria-hidden="true"
                className="inline size-8 text-gray-200 animate-spin fill-blue-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span className="sr-only">Loading...</span>
            </div>
          ) : null}
          {searchQuery ? (
            <div className="absolute right-4 top-4 -mt-0.75">
              <button
                type="button"
                className="text-gray-700 cursor-pointer"
                onClick={() => setSearchQuery('')}
              >
                <span className="sr-only">Clear search</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-7"
                  role="presentation"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col md:grid grid-cols-4 gap-2 mb-4">
          <div>
            <label htmlFor="sortOrder" className="text-xs">
              Sort By
            </label>
            <select
              id="sortOrder"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
              className="block bg-gray-200 rounded py-1.5 px-2 text-black mt-1 w-full text-sm"
            >
              <option value="relevance">Relevance</option>
              <option value="chronological">Episode Order</option>
            </select>
          </div>
          <div>
            <label htmlFor="linesOfContext" className="text-xs">
              Lines of Context
            </label>
            <input
              id="linesOfContext"
              type="number"
              value={linesOfContext}
              min="0"
              max="20"
              onChange={(e) => setLinesOfContext(Number(e.target.value))}
              className="block bg-gray-200 rounded py-1.5 px-3 text-black mt-1 w-full text-sm"
            />
          </div>
          <div>
            <label htmlFor="seasonSelector" className="text-xs">
              Filter by Season
            </label>
            <select
              id="seasonSelector"
              value={seasonFilter}
              onChange={(e) => {
                setSeasonFilter(e.target.value)
                setEpisodeFilter('')
              }}
              className="block bg-gray-200 rounded py-1.5 px-2 text-black mt-1 w-full text-sm"
            >
              <option value="">Any</option>
              {seasonSelectorData.seasons.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="episodeSelector" className="text-xs">
              Filter by Episode
            </label>
            <select
              id="episodeSelector"
              value={episodeFilter}
              onChange={(e) => setEpisodeFilter(e.target.value)}
              className="block bg-gray-200 rounded py-1.5 px-2 text-black mt-1 w-full text-sm disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!seasonFilter}
            >
              <option value="">Any</option>
              {seasonSelectorData.episodesBySeason[seasonFilter]?.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>
        </div>
        {data ? (
          (sortOrder === 'chronological'
            ? data.lineGroups.toSorted((a, b) => a.episode.localeCompare(b.episode))
            : data.lineGroups
          ).map((lineGroup) => (
            <LineGroup
              key={lineGroup.matchedLines.join('|')}
              lineGroup={lineGroup}
              episode={data.episodes[lineGroup.episode]}
            />
          ))
        ) : !isPending ? (
          <div className="text-xl p-4 rounded bg-gray-100">
            No results. Try a different search query.
          </div>
        ) : null}
        {process.env.NODE_ENV !== 'production' ? (
          <>
            <details className="mt-4">
              <summary>Raw Result</summary>
              <pre className="overflow-scroll p-4 rounded bg-gray-700 text-white text-xs max-h-96">
                <code>{JSON.stringify(data, null, 2)}</code>
              </pre>
            </details>
            <details className="mt-4">
              <summary>Search Values</summary>
              <pre className="overflow-scroll p-4 rounded bg-gray-700 text-white text-xs max-h-96">
                <code>
                  {JSON.stringify(
                    {
                      searchQuery,
                      linesOfContext,
                      seasonFilter,
                      episodeFilter,
                    },
                    null,
                    2,
                  )}
                </code>
              </pre>
            </details>
          </>
        ) : null}
      </div>
    </div>
  )
}

function Footer({ showKey }: ComponentProps<typeof App>) {
  return (
    <div className="">
      <ul className="h-10 flex justify-center divide-x divide-[var(--theme-divide-color)] text-sm p-2 items-center">
        {showKeys.map((key) => {
          const name = shows[key]
          return (
            <li key={name} className="px-2">
              <a className="text-[var(--theme-link-color)] font-light" href={`/${key}`}>
                {name}
              </a>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function LineGroup({
  lineGroup,
  episode,
}: {
  lineGroup: Results['lineGroups'][number]
  episode: Results['episodes'][string]
}) {
  return (
    <div className="paper bg-white text-black mb-6 relative shadow-lg leading-7">
      <div>
        <div className="text-sm leading-[1.6rem]">
          S{episode.season}E{episode.episode} — {episode.title}
        </div>
      </div>
      {lineGroup.lines.map((line) => {
        if (line === START_OF_EPISODE)
          return (
            <div key="start" className="text-sm text-center leading-7 italic text-gray-600">
              Start of Episode
            </div>
          )
        if (line === END_OF_EPISODE)
          return (
            <div key="end" className="text-sm text-center leading-7 italic text-gray-600">
              End of Episode
            </div>
          )

        return (
          <div
            key={line.id}
            className={`-indent-3 ml-3 ${lineGroup.matchedLines.includes(line.id) ? '' : 'text-gray-400'}`}
          >
            {line.character.length ? (
              <>
                <span className="font-bold">{line.character}</span>:{' '}
                {htmlifyHighlight(line.highlight) ?? line.quote}
              </>
            ) : (
              (htmlifyHighlight(line.highlight) ?? line.quote)
            )}
          </div>
        )
      })}
      <div className="text-xs text-right absolute right-2 bottom-1.5">
        <a
          href={episode.source}
          target="_blank"
          rel="noreferrer"
          className="text-gray-500 underline inline-flex gap-0.5 items-center"
        >
          Source
          <span className="sr-only">External Link</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="size-4"
            role="presentation"
          >
            <path d="M6.22 8.72a.75.75 0 0 0 1.06 1.06l5.22-5.22v1.69a.75.75 0 0 0 1.5 0v-3.5a.75.75 0 0 0-.75-.75h-3.5a.75.75 0 0 0 0 1.5h1.69L6.22 8.72Z" />
            <path d="M3.5 6.75c0-.69.56-1.25 1.25-1.25H7A.75.75 0 0 0 7 4H4.75A2.75 2.75 0 0 0 2 6.75v4.5A2.75 2.75 0 0 0 4.75 14h4.5A2.75 2.75 0 0 0 12 11.25V9a.75.75 0 0 0-1.5 0v2.25c0 .69-.56 1.25-1.25 1.25h-4.5c-.69 0-1.25-.56-1.25-1.25v-4.5Z" />
          </svg>
        </a>
      </div>
    </div>
  )
}

function Highlight({ children }: PropsWithChildren) {
  return <span className="bg-amber-200">{children}</span>
}

function htmlifyHighlight(highlight?: string) {
  if (!highlight) return
  const regex = /<HI>(.*?)<\/HI>/g
  const result = []
  let mutableHighlight = highlight

  for (const match of [...highlight.matchAll(regex)].reverse()) {
    const indexAfterMatch = match.index + match[0].length
    result.unshift(mutableHighlight.slice(indexAfterMatch))
    result.unshift(<Highlight>{match[1]}</Highlight>)
    mutableHighlight = mutableHighlight.slice(0, match.index)
  }
  result.unshift(mutableHighlight)

  return result
}

async function fetchSearchResults(
  searchQuery: string,
  {
    episodeFilter,
    linesOfContext,
    seasonFilter,
  }: { linesOfContext: number; seasonFilter: string; episodeFilter: string },
  { showKey, signal }: { showKey: ShowKey; signal: AbortSignal },
) {
  const payload: SearchPayload = {
    query: searchQuery,
    linesOfContext,
    show: showKey,
  }
  if (seasonFilter) {
    payload.filter = `s${seasonFilter}`
    if (episodeFilter) {
      payload.filter += `e${episodeFilter}`
    }
  }
  const response = await fetch('/api/search', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'content-type': 'application/json' },
    signal,
  })
  const json = await response.json()
  return json as Results
}
